import Vapi from 'vuex-rest-api';
import axios from 'axios';
import { pathOr, findIndex } from 'lodash/fp';

// Make Django csrf work
axios.defaults.xsrfCookieName = 'csrftoken';
axios.defaults.xsrfHeaderName = 'X-CSRFToken';

const addToCollection = (collectionName, withDetails = false) => (
  state,
  { data }
) => {
  const index = findIndex({ id: data.id }, state[collectionName]);
  const item = {
    ...pathOr({}, [collectionName, index], state),
    ...data,
    hasDetails:
      withDetails ||
      pathOr(withDetails, [collectionName, index, 'hasDetails'], state),
  };

  if (index === -1) {
    state[collectionName].push(item);
  } else {
    state[collectionName].splice(index, 1, item);
  }
};

const removeFromCollection = (collectionName) => (state, { data }) => {
  state[collectionName] = state[collectionName].filter(
    (item) => item.id != data.id
  );
};

export default new Vapi({
  axios: axios.create({ withCredentials: true }),
  state: {
    playlists: [],
    user: window.__DATA__.user,
    programms: [],
    liveStreams: [],
    physio_programms: [],
    patients: [],
    physios: [],
    trainings: [],
    physioRequests: [],
    physio_videos: [],
    physio_physioRequests: [],
  },
})
  /* Patient */
  .get({
    action: 'getPlaylists',
    property: 'playlists',
    path: '/api/playlists/?fields!=videos,lastFinishDate',
    onSuccess: (state, { data }) => {
      state._playlists_are_loaded = true;
      data.map((data) => addToCollection('playlists')(state, { data }));
    },
  })
  .get({
    action: 'getTrainings',
    property: 'trainings',
    path: '/api/trainings/?isVideoTraining=true',
    onSuccess: (state, { data }) => {
      data.map((data) => addToCollection('trainings')(state, { data }));
    },
  })
  .get({
    action: 'getLiveStreams',
    property: 'liveStreams',
    path: '/api/livestreams/',
  })
  .get({
    action: 'getUser',
    property: 'user',
    path: '/api/user/me/',
  })
  .put({
    action: 'updateUser',
    property: 'user',
    path: '/api/users/me/',
  })
  .post({
    action: 'addBasfi',
    property: 'user',
    path: '/api/users/me/save_basfi/',
  })
  .post({
    action: 'api_updateUserFav',
    property: 'user',
    path: '/api/users/me/update_fav/',
  })
  .post({
    action: 'api_ratePlaylist',
    property: 'playlists',
    path: ({ playlistId }) => `/api/playlists/${playlistId}/rate_playlist/`,
    onSuccess: addToCollection('playlists', true),
  })
  .post({
    action: 'finishPlaylist',
    property: 'playlists',
    path: ({ id }) => `/api/playlists/${id}/finish/`,
    onSuccess: addToCollection('playlists', true),
  })
  .post({
    action: 'finishProgramm',
    property: 'programms',
    path: ({ id }) => `/api/programms/${id}/finish/`,
    onSuccess: addToCollection('programms', true),
  })
  .post({
    action: 'loginUser',
    property: 'user',
    path: '/api/users/me/login/',
  })
  .post({
    action: 'signupUser',
    property: 'user',
    path: '/api/signup',
  })
  .post({
    action: 'api_resetPassword',
    property: 'user',
    path: '/api/reset_password',
  })
  .post({
    action: 'api_askPhysio',
    property: 'askPhysio',
    path: '/api/ask_physio',
  })
  .get({
    action: 'logoutUser',
    property: 'user',
    path: '/api/users/me/logout/',
    onSuccess: (state) => (state.user = null),
  })
  .get({
    action: 'api_getPlaylist',
    property: 'playlists',
    path: ({ id }) => `/api/playlists/${id}/`,
    onSuccess: addToCollection('playlists', true),
  })

  /* --- Feature: physio --- */
  .get({
    action: 'api_getProgramms',
    property: 'programms',
    path: '/api/programms/?fields!=videos',
    onSuccess: (state, { data }) => {
      data.map((data) => addToCollection('programms')(state, { data }));
    },
  })
  .get({
    action: 'api_getProgramm',
    property: 'programms',
    path: ({ id }) => `/api/programms/${id}/`,
    onSuccess: addToCollection('programms', true),
  })
  .get({
    action: 'searchPhysios',
    property: 'physios',
    path: ({ search }) => `/api/physios/?search=${search}`,
  })
  .get({
    action: 'loadPhysioRequests',
    property: 'physioRequests',
    path: '/api/physiorequests/',
  })
  .post({
    action: 'sendPhysioRequest',
    property: 'physioRequests',
    path: '/api/physiorequests/',
    onSuccess: addToCollection('physioRequests'),
  })
  .get({
    action: 'retractPhysioRequest',
    property: 'physioRequests',
    path: ({ id }) => `/api/physiorequests/${id}/retract/`,
    onSuccess: removeFromCollection('physioRequests'),
  })

  /* Physio */
  .get({
    action: 'physio_loadVideos',
    property: 'physio_videos',
    path: '/api/physio/videos/',
  })
  .get({
    action: 'getPhysioProgramms',
    property: 'physio_programms',
    path: '/api/physio/programms/',
  })
  .post({
    action: 'physio_updatePhysioInfo',
    property: 'user',
    path: '/api/users/me/update_physio_info/',
  })
  .get({
    action: 'physio_loadProgramm',
    property: 'physio_programms',
    path: ({ id }) => `/api/physio/programms/${id}/`,
    onSuccess: addToCollection('physio_programms'),
  })
  .post({
    action: 'physio_createProgramm',
    path: '/api/physio/programms/new/',
    onSuccess: addToCollection('physio_programms'),
  })
  .patch({
    action: 'physio_updateProgramm',
    path: ({ id }) => `/api/physio/programms/${id}/`,
    property: 'physio_programms',
    onSuccess: addToCollection('physio_programms'),
  })
  .get({
    action: 'physio_loadPhysioRequests',
    property: 'physio_physioRequests',
    path: '/api/physio/requests/', //?fields=accepted,created,id,patientFirstName,patientLastName,patientId',
  })
  .get({
    action: 'physio_loadPhysioRequest',
    property: 'physio_physioRequests',
    path: ({ id }) => `/api/physio/requests/${id}/`,
    onSuccess: addToCollection('physio_physioRequests'),
  })
  .get({
    action: 'physio_acceptPhysioRequest',
    property: 'physio_physioRequests',
    path: ({ id }) => `/api/physio/requests/${id}/accept/`,
    onSuccess: addToCollection('physio_physioRequests'),
  })
  .get({
    action: 'physio_rejectPhysioRequest',
    property: 'physio_physioRequests',
    path: ({ id }) => `/api/physio/requests/${id}/reject/`,
    onSuccess: removeFromCollection('physio_physioRequests'),
  });



// WEBPACK FOOTER //
// ./src/store/api.js